// TalentManagement.js
import Momentsia_logo from "../images/Momentsia _logo.png";
import React, { useEffect, useRef, useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import MicIcon from "@mui/icons-material/Mic";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import SpeakerIcon from "@mui/icons-material/Speaker";
import CelebrationIcon from "@mui/icons-material/Celebration";
import CampaignIcon from "@mui/icons-material/Campaign";
import BadgeIcon from "@mui/icons-material/Badge";
import InstagramIcon from "@mui/icons-material/Instagram";
import emailjs from '@emailjs/browser';

const T1 = "#0ecfb0";
const T2 = "#0aa896";
const T3 = "#077a6e";
const NAVY = "#0a0e23";
const NAVY_DEEP = "#080b1c";
const GOLD = "#d7ab77";
const DARK = "#1a1a1a";
const TEAL_GRAD = `linear-gradient(135deg, ${T1}, ${T3})`;

// EmailJS Configuration
const EMAILJS_SERVICE_ID = "service_zieecow";
const EMAILJS_TEMPLATE_ID = "template_l1xmaal";
const EMAILJS_PUBLIC_KEY = "q2O06yijgnOJTch1m";

// ---------- ANIMATED CONSTELLATION BACKGROUND ----------
const ParticleNetwork = ({ particleColor = GOLD, lineColor = "215,171,119", maxParticles = 130 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const parent = canvas.parentElement;
    let width = 0, height = 0, particles = [], animationId = null;
    const prefersReducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const buildParticles = () => {
      const area = width * height;
      const count = Math.min(Math.floor(area / 9000), maxParticles);
      particles = Array.from({ length: Math.max(count, 24) }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r: Math.random() * 1.3 + 0.6,
      }));
    };

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildParticles();
    };

    const step = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x <= 0 || p.x >= width) p.vx *= -1;
        if (p.y <= 0 || p.y >= height) p.vy *= -1;
      });
      const maxDist = 125;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i], b = particles[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDist) {
            ctx.strokeStyle = `rgba(${lineColor},${0.16 * (1 - dist / maxDist)})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = particleColor;
        ctx.globalAlpha = 0.85;
        ctx.fill();
        ctx.globalAlpha = 1;
      });
      if (!prefersReducedMotion) animationId = requestAnimationFrame(step);
    };

    resize();
    step();
    window.addEventListener("resize", resize);
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, [particleColor, lineColor, maxParticles]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", pointerEvents: "none" }}
    />
  );
};

const MomentsiaLogo = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
    <img src={Momentsia_logo} alt="Momentsia" style={{ height: 48, width: "auto", objectFit: "contain" }} />
  </div>
);

// ---------- SHOW / TALENT CATEGORIES ----------
const showCategories = [
  { key: "all", label: "All Shows", icon: <StarIcon sx={{ fontSize: 18 }} /> },
  { key: "stage", label: "Stage Shows", icon: <MicIcon sx={{ fontSize: 18 }} /> },
  { key: "party", label: "Party & Club Shows", icon: <HeadphonesIcon sx={{ fontSize: 18 }} /> },
  { key: "band", label: "Live Bands", icon: <MusicNoteIcon sx={{ fontSize: 18 }} /> },
  { key: "anchor", label: "Anchors & Hosts", icon: <RecordVoiceOverIcon sx={{ fontSize: 18 }} /> },
  { key: "dance", label: "Dance Troupes", icon: <EmojiPeopleIcon sx={{ fontSize: 18 }} /> },
  { key: "comedy", label: "Comedy & Standup", icon: <TheaterComedyIcon sx={{ fontSize: 18 }} /> },
  { key: "celebrity", label: "Celebrities", icon: <VerifiedIcon sx={{ fontSize: 18 }} /> },
];

// ---------- ARTIST / TALENT LISTINGS (COMPLETE UPDATED LIST) ----------
const artists = [
  // ===== STAGE SHOWS - Bollywood/Pop Artists =====
  { 
    name: "Tushar Joshi", 
    category: "stage", 
    genre: "Playback Singer", 
    img: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=600&h=700&fit=crop", 
    tag: "Bollywood Hits", 
    verified: true, 
    rating: 4.9, 
    location: "Mumbai",
    instagram: "https://www.instagram.com/tusharjoshiii"
  },
  { 
    name: "Ashking", 
    category: "stage", 
    genre: "Singer & Performer", 
    img: "https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?w=600&h=700&fit=crop", 
    tag: "Pop & Bollywood", 
    verified: true, 
    rating: 4.8, 
    location: "Mumbai" 
  },
  { 
    name: "Nikita Gandhi", 
    category: "stage", 
    genre: "Playback Singer", 
    img: "https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=600&h=700&fit=crop", 
    tag: "Bollywood & Sufi", 
    verified: true, 
    rating: 4.9, 
    location: "Mumbai" 
  },
  { 
    name: "Saswat Singh", 
    category: "stage", 
    genre: "Singer & Performer", 
    img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&h=700&fit=crop", 
    tag: "Bollywood & Indie", 
    verified: true, 
    rating: 4.7, 
    location: "Mumbai" 
  },
  { 
    name: "Shreya Jain", 
    category: "stage", 
    genre: "Playback Singer", 
    img: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=600&h=700&fit=crop", 
    tag: "Bollywood & Classical", 
    verified: true, 
    rating: 4.8, 
    location: "Mumbai" 
  },
  { 
    name: "Kavya Malik", 
    category: "stage", 
    genre: "Singer & Performer", 
    img: "https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?w=600&h=700&fit=crop", 
    tag: "Pop & Bollywood", 
    verified: true, 
    rating: 4.7, 
    location: "Delhi NCR" 
  },
  { 
    name: "Mohan Sisters", 
    category: "stage", 
    genre: "Duo Singers", 
    img: "https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=600&h=700&fit=crop", 
    tag: "Bollywood & Folk", 
    verified: true, 
    rating: 4.8, 
    location: "Mumbai" 
  },
  { 
    name: "Antara Mitra", 
    category: "stage", 
    genre: "Playback Singer", 
    img: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=600&h=700&fit=crop", 
    tag: "Bollywood & Bengali", 
    verified: true, 
    rating: 4.9, 
    location: "Kolkata" 
  },
  { 
    name: "Madhubanti Bagchi", 
    category: "stage", 
    genre: "Playback Singer", 
    img: "https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?w=600&h=700&fit=crop", 
    tag: "Bollywood & Bengali", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata" 
  },
  { 
    name: "Sona Mahapatra", 
    category: "stage", 
    genre: "Playback Singer", 
    img: "https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=600&h=700&fit=crop", 
    tag: "Sufi & Bollywood", 
    verified: true, 
    rating: 4.8, 
    location: "Mumbai" 
  },

  // ===== STAGE SHOWS - Bengali Artists =====
  { 
    name: "Ananya Chakraborty", 
    category: "stage", 
    genre: "Bengali Playback Singer", 
    img: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=600&h=700&fit=crop", 
    tag: "Bengali Modern", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata" 
  },
  { 
    name: "Somlata Acharya", 
    category: "stage", 
    genre: "Bengali Playback Singer", 
    img: "https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?w=600&h=700&fit=crop", 
    tag: "Bengali Classical", 
    verified: true, 
    rating: 4.9, 
    location: "Kolkata" 
  },
  { 
    name: "Anupom Roy", 
    category: "stage", 
    genre: "Singer-Songwriter", 
    img: "https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=600&h=700&fit=crop", 
    tag: "Bengali Indie", 
    verified: true, 
    rating: 4.9, 
    location: "Kolkata" 
  },
  { 
    name: "Raghav Chatterjee", 
    category: "stage", 
    genre: "Playback Singer", 
    img: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=600&h=700&fit=crop", 
    tag: "Bengali & Bollywood", 
    verified: true, 
    rating: 4.7, 
    location: "Kolkata" 
  },
  { 
    name: "Sneha", 
    category: "stage", 
    genre: "Playback Singer", 
    img: "https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?w=600&h=700&fit=crop", 
    tag: "Bengali Modern", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata" 
  },
  { 
    name: "Subhamita Banerjee", 
    category: "stage", 
    genre: "Playback Singer", 
    img: "https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=600&h=700&fit=crop", 
    tag: "Bengali & Bollywood", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata" 
  },
  { 
    name: "Jayati Chakraborty", 
    category: "stage", 
    genre: "Playback Singer", 
    img: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=600&h=700&fit=crop", 
    tag: "Bengali Classical", 
    verified: true, 
    rating: 4.9, 
    location: "Kolkata" 
  },

  // ===== CELEBRITIES (Manage By Momentsia) =====
  { 
    name: "The Solstice", 
    category: "celebrity", 
    genre: "Band", 
    img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&h=700&fit=crop", 
    tag: "Indie Rock Band", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata",
    instagram: "https://www.instagram.com/_.solstice.ind._" 
  },
  { 
    name: "Sugar Crush", 
    category: "celebrity", 
    genre: "Band", 
    img: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=700&fit=crop", 
    tag: "Pop Rock Band", 
    verified: true, 
    rating: 4.7, 
    location: "Kolkata",
    instagram: "https://www.instagram.com/sugarcrush.band" 
  },
  { 
    name: "UIC (Universe In A Crayon)", 
    category: "celebrity", 
    genre: "Band", 
    img: "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=600&h=700&fit=crop", 
    tag: "Indie Experimental", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata",
    instagram: "https://www.instagram.com/universeinacrayon" 
  },
  { 
    name: "Mitra On Vocals", 
    category: "celebrity", 
    genre: "Singer", 
    img: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=600&h=700&fit=crop", 
    tag: "Bollywood & Indie", 
    verified: true, 
    rating: 4.9, 
    location: "Kolkata",
    instagram: "https://www.instagram.com/mitraonvocals" 
  },
  { 
    name: "Manzil x Sougata Sengupta", 
    category: "celebrity", 
    genre: "Singer", 
    img: "https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?w=600&h=700&fit=crop", 
    tag: "Fusion Music", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata",
    instagram: "https://www.instagram.com/iamsaugatasengupta" 
  },

  // ===== LIVE BANDS =====
  { 
    name: "Cactus", 
    category: "band", 
    genre: "Rock Band", 
    img: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=700&fit=crop", 
    tag: "Bengali Rock", 
    verified: true, 
    rating: 4.9, 
    location: "Kolkata" 
  },
  { 
    name: "Tal Patar Sepahi", 
    category: "band", 
    genre: "Rock Band", 
    img: "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=600&h=700&fit=crop", 
    tag: "Bengali Rock", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata" 
  },
  { 
    name: "Underground Authority", 
    category: "band", 
    genre: "Rock Band", 
    img: "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=600&h=700&fit=crop", 
    tag: "Indian Rock", 
    verified: true, 
    rating: 4.8, 
    location: "Mumbai" 
  },
  { 
    name: "ARC Projects", 
    category: "band", 
    genre: "Fusion Band", 
    img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&h=700&fit=crop", 
    tag: "Indie Fusion", 
    verified: true, 
    rating: 4.7, 
    location: "Kolkata" 
  },
  { 
    name: "Adil Khan", 
    category: "band", 
    genre: "Folk Fusion Band", 
    img: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=700&fit=crop", 
    tag: "Sufi Folk", 
    verified: true, 
    rating: 4.8, 
    location: "Delhi NCR" 
  },
  { 
    name: "Lakhhichhara", 
    category: "band", 
    genre: "Folk Rock Band", 
    img: "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=600&h=700&fit=crop", 
    tag: "Bengali Folk", 
    verified: true, 
    rating: 4.7, 
    location: "Kolkata" 
  },
  { 
    name: "Holyganism", 
    category: "band", 
    genre: "Rock Band", 
    img: "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=600&h=700&fit=crop", 
    tag: "Bengali Rock", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata" 
  },

  // ===== PARTY & CLUB SHOWS - DJs =====
  { 
    name: "DJ Rohan Verve", 
    category: "party", 
    genre: "EDM / Bollywood DJ", 
    img: "https://images.unsplash.com/photo-1571266028243-d220c9c3b31d?w=600&h=700&fit=crop", 
    tag: "Club & Sangeet Sets", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata" 
  },
  { 
    name: "DJ Priyanka X", 
    category: "party", 
    genre: "Commercial & House DJ", 
    img: "https://images.unsplash.com/photo-1571266028243-e5a3e3d3e3d2?w=600&h=700&fit=crop", 
    tag: "Ladies Night Specialist", 
    verified: true, 
    rating: 4.6, 
    location: "Mumbai" 
  },
  { 
    name: "Bass Bhangra Crew", 
    category: "party", 
    genre: "Dhol + DJ Fusion", 
    img: "https://images.unsplash.com/photo-1519683384663-c9b34271669c?w=600&h=700&fit=crop", 
    tag: "Baraat & Sangeet", 
    verified: false, 
    rating: 4.7, 
    location: "Delhi NCR" 
  },
  { 
    name: "DJ Kabir Nox", 
    category: "party", 
    genre: "Retro & Bollywood DJ", 
    img: "https://images.unsplash.com/photo-1598387993441-a364f854c3e1?w=600&h=700&fit=crop", 
    tag: "Corporate After-Party", 
    verified: true, 
    rating: 4.5, 
    location: "Kolkata" 
  },

  // ===== ANCHORS & HOSTS =====
  { 
    name: "Ananya Roy", 
    category: "anchor", 
    genre: "Corporate & Wedding Anchor", 
    img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=700&fit=crop", 
    tag: "Bilingual Hosting", 
    verified: true, 
    rating: 4.9, 
    location: "Kolkata" 
  },
  { 
    name: "Debjit Banerjee", 
    category: "anchor", 
    genre: "Stage Anchor & Emcee", 
    img: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=600&h=700&fit=crop", 
    tag: "Award Nights", 
    verified: true, 
    rating: 4.7, 
    location: "Delhi NCR" 
  },
  { 
    name: "Sohini Dutta", 
    category: "anchor", 
    genre: "Wedding Host", 
    img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&h=700&fit=crop", 
    tag: "Sangeet & Reception", 
    verified: false, 
    rating: 4.6, 
    location: "Kolkata" 
  },

  // ===== DANCE TROUPES =====
  { 
    name: "Nritya Nova", 
    category: "dance", 
    genre: "Bollywood Dance Troupe", 
    img: "https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?w=600&h=700&fit=crop", 
    tag: "Sangeet Choreography", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata" 
  },
  { 
    name: "Urban Beatz Crew", 
    category: "dance", 
    genre: "Hip-Hop & Freestyle", 
    img: "https://images.unsplash.com/photo-1547153760-18fc86324498?w=600&h=700&fit=crop", 
    tag: "Product Launch Acts", 
    verified: true, 
    rating: 4.7, 
    location: "Mumbai" 
  },
  { 
    name: "Classique Movers", 
    category: "dance", 
    genre: "Fusion Classical Dance", 
    img: "https://images.unsplash.com/photo-1518834107812-67b0b7c58434?w=600&h=700&fit=crop", 
    tag: "Cultural Functions", 
    verified: false, 
    rating: 4.6, 
    location: "Bengaluru" 
  },

  // ===== COMEDY & STANDUP =====
  { 
    name: "Rajarshi Mukherjee", 
    category: "comedy", 
    genre: "Standup Comedian", 
    img: "https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=600&h=700&fit=crop", 
    tag: "Corporate Comedy Nights", 
    verified: true, 
    rating: 4.8, 
    location: "Kolkata" 
  },
  { 
    name: "Tanvi Iyer", 
    category: "comedy", 
    genre: "Improv & Standup", 
    img: "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=600&h=700&fit=crop", 
    tag: "Roasts & Crowd Work", 
    verified: false, 
    rating: 4.5, 
    location: "Mumbai" 
  },
];

// ---------- AVAILABLE SERVICES ----------
const availableServices = [
  { icon: <EventAvailableIcon sx={{ fontSize: 30 }} />, title: "Artist Booking & Curation", desc: "Handpicked shortlists matched to your event's genre, budget and audience." },
  { icon: <SpeakerIcon sx={{ fontSize: 30 }} />, title: "Sound, Light & Stage Setup", desc: "Full production support — PA systems, lighting rigs and stage design." },
  { icon: <CelebrationIcon sx={{ fontSize: 30 }} />, title: "Show Direction & Rundown", desc: "Set-list planning, act sequencing and on-ground show coordination." },
  { icon: <BadgeIcon sx={{ fontSize: 30 }} />, title: "Contracts & Artist Logistics", desc: "Agreements, travel, accommodation and green-room hospitality handled end-to-end." },
  { icon: <CampaignIcon sx={{ fontSize: 30 }} />, title: "Promotion & PR Support", desc: "Show announcements, media coverage and social promotion for ticketed events." },
  { icon: <CheckCircleIcon sx={{ fontSize: 30 }} />, title: "On-Site Show Management", desc: "A dedicated Momentsia coordinator present at your event from soundcheck to wrap." },
];

// ---------- LOCATIONS WE COVER ----------
const locations = [
  { city: "Kolkata", note: "Home base — fastest turnaround & largest roster", primary: true },
  { city: "Delhi NCR", note: "Full artist & production support" },
  { city: "Mumbai", note: "Full artist & production support" },
  { city: "Bengaluru", note: "Curated roster, travel-in artists" },
  { city: "Pune", note: "On request, travel-in artists" },
  { city: "Pan-India", note: "Destination weddings & corporate offsites" },
];

// ---------- BOOKING DIALOG WITH EMAILJS ----------
const BookArtistDialog = ({ open, onClose, artistName }) => {
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", date: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({ name: "", phone: "", email: "", date: "", message: "" });
      setErrors({});
      setStatus("");
    }
  }, [open]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = "Name is required";
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.phone?.trim()) newErrors.phone = "Phone number is required";
    if (!formData.date) newErrors.date = "Event date is required";
    setErrors(newErrors);
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    setStatus("Sending...");

    const templateParams = {
      from_name: formData.name.trim(),
      from_email: formData.email.trim(),
      from_phone: formData.phone?.trim() || "Not provided",
      event_type: `Talent Booking – ${artistName || "General Enquiry"}`,
      event_date: formData.date,
      guests: "Not specified",
      message: formData.message?.trim() || "No additional message provided",
      submitted_at: new Date().toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setStatus("✓ Request sent successfully! We'll contact you within 24 hours.");
      setTimeout(() => {
        setStatus("");
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Booking - Failed to send email:", error);
      setStatus("❌ Failed to send. Please try again or call us directly.");
      setTimeout(() => setStatus(""), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} />
      <div style={{ position: "relative", background: "#fff", borderRadius: 14, padding: "40px 36px", maxWidth: 480, width: "90%", boxShadow: "0 24px 64px rgba(0,0,0,0.25)", maxHeight: "90vh", overflowY: "auto" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 18, background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#aaa" }}>✕</button>
        <div style={{ height: 4, background: TEAL_GRAD, borderRadius: 4, marginBottom: 24 }} />
        <h3 style={{ margin: "0 0 6px", color: DARK, fontWeight: 800, fontSize: 20 }}>
          {artistName ? `Book ${artistName}` : "Book This Talent"}
        </h3>
        <p style={{ color: "#888", fontSize: 13, margin: "0 0 22px" }}>Share your details and we'll confirm availability within 24 hours.</p>

        <form onSubmit={handleSubmit}>
          <input type="text" name="name" placeholder="Full Name *" value={formData.name} onChange={handleChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${errors.name ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, boxSizing: "border-box" }} />
          {errors.name && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{errors.name}</p>}

          <input type="tel" name="phone" placeholder="Phone Number *" value={formData.phone} onChange={handleChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${errors.phone ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, boxSizing: "border-box" }} />
          {errors.phone && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{errors.phone}</p>}

          <input type="email" name="email" placeholder="Email Address *" value={formData.email} onChange={handleChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${errors.email ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, boxSizing: "border-box" }} />
          {errors.email && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{errors.email}</p>}

          <input type="date" name="date" value={formData.date} onChange={handleChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${errors.date ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14 }} />
          {errors.date && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{errors.date}</p>}

          <textarea name="message" placeholder="Venue, city, show duration, budget range..." rows={3} value={formData.message} onChange={handleChange}
            style={{ display: "block", width: "100%", marginBottom: 18, padding: "11px 14px", border: "1.5px solid #e0e0e0", borderRadius: 7, fontSize: 14, resize: "vertical" }} />

          <button type="submit" disabled={isSubmitting}
            style={{ width: "100%", padding: "13px 0", background: TEAL_GRAD, color: "#fff", border: "none", borderRadius: 7, fontWeight: 700, fontSize: 15, cursor: isSubmitting ? "not-allowed" : "pointer", opacity: isSubmitting ? 0.7 : 1 }}>
            {isSubmitting ? "Submitting..." : "Request Booking"}
          </button>
          {status && <p style={{ marginTop: 16, fontSize: 12, textAlign: "center", color: status.includes("✓") ? T2 : T1 }}>{status}</p>}
        </form>
      </div>
    </div>
  );
};

export default function TalentManagement() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [bookOpen, setBookOpen] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(null);

  const filteredArtists = activeCategory === "all" ? artists : artists.filter(a => a.category === activeCategory);

  const openBooking = (artistName = null) => {
    setSelectedArtist(artistName);
    setBookOpen(true);
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", margin: 0, padding: 0, color: DARK }}>
      <link href="https://fonts.googleapis.com/css2?family=Allura&display=swap" rel="stylesheet" />
      <style>{`
        *{box-sizing:border-box;}body{margin:0;}
        h1, h2 { font-family: 'Allura', cursive !important; letter-spacing: 1px; }
        h3, h4, h5, h6, p, a, li, button, input, textarea, .description { font-family: 'Segoe UI', Arial, sans-serif; }
        .cb:hover{opacity:0.88;transform:translateY(-1px);}
        .filter-chip{transition:all .25s;}
        .filter-chip:hover{transform:translateY(-2px);}
        .a-card{transition:transform .3s, box-shadow .3s;}
        .a-card:hover{transform:translateY(-8px);box-shadow:0 22px 44px rgba(14,207,176,0.25)!important;}
        .a-card:hover img{transform:scale(1.06);}
        .a-card .a-img{overflow:hidden;}
        .a-card img{transition:transform .5s;}
        .book-btn:hover{background:${T1}!important;color:#fff!important;}
        .instagram-link:hover{color:#E4405F!important;}
        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes floatOrb{0%,100%{transform:translateY(0) translateX(0);}50%{transform:translateY(-22px) translateX(14px);}}
        @keyframes pulseOrb{0%,100%{opacity:0.7;transform:scale(1);}50%{opacity:1;transform:scale(1.12);}}
        .hcap{animation:fadeUp 0.7s ease both;}
        @media(max-width:800px){.office-map-grid{grid-template-columns:1fr!important;}}
      `}</style>

      {/* Navigation Component */}
      <Navigation onPlanEventClick={() => openBooking()} />

      {/* HERO */}
      <div style={{ position: "relative", width: "100%", background: "#000" }}>
        <div style={{ position: "relative", width: "100%", height: "70vh", minHeight: 440, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920&q=80&fit=crop" alt="Live show"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 1 }} />
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(155deg,rgba(7,122,110,0.6) 0%,rgba(0,0,0,0.35) 55%,rgba(0,0,0,0.55) 100%)`, zIndex: 2 }} />
          <div className="hcap" style={{ position: "relative", zIndex: 3, textAlign: "center", width: "90%", maxWidth: 880, margin: "0 auto" }}>
            <div style={{ width: 60, height: 3.5, background: TEAL_GRAD, borderRadius: 2, margin: "0 auto 18px" }} />
            <h1 style={{ color: "#fff", fontSize: "clamp(1.8rem,5vw,3.2rem)", fontWeight: 800, lineHeight: 1.2, textShadow: "0 2px 20px rgba(0,0,0,0.5)", margin: "0 0 14px" }}>
              Every Show, One Stage of Talent
            </h1>
            <p style={{ color: "rgba(255,255,255,0.9)", fontSize: "clamp(0.9rem,2vw,1.15rem)", margin: "0 0 28px", maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}>
              From stage concerts to club nights, DJs to anchors, dancers to comedians — book verified performers for every kind of show.
            </p>
            <button className="cb" onClick={() => openBooking()} style={{ background: TEAL_GRAD, color: "#fff", border: "none", borderRadius: 8, padding: "14px 40px", fontWeight: 700, fontSize: 15, cursor: "pointer", boxShadow: `0 8px 26px rgba(14,207,176,0.45)` }}>
              Book a Talent
            </button>
          </div>
        </div>
      </div>

      {/* CATEGORY FILTERS */}
      <section style={{ position: "relative", background: NAVY, padding: "44px 24px 30px", textAlign: "center", overflow: "hidden" }}>
        <ParticleNetwork />
        <div style={{ position: "relative", zIndex: 1 }}>
        <p style={{ color: T1, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 3, margin: "0 0 8px" }}>Browse By Show Type</p>
        <h2 style={{ color: "#fff", fontSize: "clamp(1.4rem,3vw,2rem)", fontWeight: 800, margin: "0 0 24px" }}>All Types of Shows, One Roster</h2>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 12, maxWidth: 1000, margin: "0 auto" }}>
          {showCategories.map(cat => (
            <button
              key={cat.key}
              className="filter-chip"
              onClick={() => setActiveCategory(cat.key)}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 20px", borderRadius: 30, fontSize: 13.5, fontWeight: 700, cursor: "pointer",
                border: activeCategory === cat.key ? "none" : "1.5px solid rgba(255,255,255,0.25)",
                background: activeCategory === cat.key ? TEAL_GRAD : "rgba(255,255,255,0.05)",
                color: activeCategory === cat.key ? "#fff" : "rgba(255,255,255,0.75)",
              }}
            >
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>
        </div>
      </section>

      {/* ARTIST GRID */}
      <section style={{ position: "relative", overflow: "hidden", padding: "60px 24px 70px", background: `linear-gradient(160deg, ${NAVY}, ${NAVY_DEEP})` }}>
        <ParticleNetwork maxParticles={90} />
        <div style={{ position: "absolute", top: "-10%", right: "-6%", width: 320, height: 320, borderRadius: "50%", background: `radial-gradient(circle, rgba(215,171,119,0.16), transparent 70%)`, filter: "blur(30px)", animation: "floatOrb 11s ease-in-out infinite", pointerEvents: "none", zIndex: 0 }} />
        <div style={{ position: "absolute", bottom: "-14%", left: "-6%", width: 260, height: 260, borderRadius: "50%", background: `radial-gradient(circle, rgba(215,171,119,0.12), transparent 70%)`, filter: "blur(30px)", animation: "floatOrb 13s ease-in-out infinite reverse", pointerEvents: "none", zIndex: 0 }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto" }}>
          <p style={{ textAlign: "center", color: "rgba(255,255,255,0.65)", fontSize: 13.5, margin: "0 0 34px" }}>
            Showing <strong style={{ color: GOLD }}>{filteredArtists.length}</strong> {activeCategory === "all" ? "artists across all shows" : `artists for ${showCategories.find(c => c.key === activeCategory)?.label}`}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 28 }}>
            {filteredArtists.map(artist => (
              <div key={artist.name} className="a-card" style={{ background: "#fff", borderRadius: 18, overflow: "hidden", boxShadow: "0 14px 34px rgba(0,0,0,0.35)" }}>
                <div className="a-img" style={{ position: "relative", height: 300 }}>
                  <img src={artist.img} alt={artist.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <div style={{ position: "absolute", top: 14, left: 14, background: "rgba(13,27,42,0.75)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 20, backdropFilter: "blur(4px)" }}>
                    {showCategories.find(c => c.key === artist.category)?.label}
                  </div>
                  {artist.verified && (
                    <div style={{ position: "absolute", top: 14, right: 14, background: TEAL_GRAD, color: "#fff", fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 20, display: "flex", alignItems: "center", gap: 4 }}>
                      <VerifiedIcon sx={{ fontSize: 13 }} /> Verified
                    </div>
                  )}
                </div>
                <div style={{ padding: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <h3 style={{ fontSize: 17, fontWeight: 800, color: DARK, margin: 0 }}>{artist.name}</h3>
                    <span style={{ display: "flex", alignItems: "center", gap: 3, color: "#f5a623", fontSize: 13, fontWeight: 700 }}>
                      <StarIcon sx={{ fontSize: 15 }} /> {artist.rating}
                    </span>
                  </div>
                  <p style={{ color: T3, fontSize: 13, fontWeight: 600, margin: "0 0 10px" }}>{artist.genre}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#888", fontSize: 12, marginBottom: 10 }}>
                    <LocationOnIcon sx={{ fontSize: 15, color: T1 }} /> Based in {artist.location}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                    <div style={{ display: "inline-block", background: "#eafbf8", color: T3, fontSize: 11.5, fontWeight: 600, padding: "4px 10px", borderRadius: 16 }}>
                      {artist.tag}
                    </div>
                    {artist.instagram && (
                      <a 
                        href={artist.instagram} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="instagram-link"
                        style={{ 
                          display: "inline-flex", 
                          alignItems: "center", 
                          gap: 4, 
                          color: T2, 
                          fontSize: 12, 
                          fontWeight: 600,
                          textDecoration: "none",
                          transition: "color 0.3s"
                        }}
                      >
                        <InstagramIcon sx={{ fontSize: 16 }} /> Follow
                      </a>
                    )}
                  </div>
                  <button
                    className="book-btn"
                    onClick={() => openBooking(artist.name)}
                    style={{ width: "100%", padding: "10px 0", background: "#fff", color: T2, border: `1.5px solid ${T1}`, borderRadius: 8, fontWeight: 700, fontSize: 13.5, cursor: "pointer" }}
                  >
                    Check Availability
                  </button>
                </div>
              </div>
            ))}
          </div>
          {filteredArtists.length === 0 && (
            <p style={{ textAlign: "center", color: "rgba(255,255,255,0.6)", fontSize: 15, padding: "60px 0" }}>No artists found in this category yet — check back soon.</p>
          )}
        </div>
      </section>

      {/* AVAILABLE SERVICES */}
      <section style={{ background: "#fff", padding: "70px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 46 }}>
            <p style={{ color: T2, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 2.5 }}>What's Included</p>
            <h2 style={{ fontSize: "clamp(1.4rem,3vw,2.1rem)", fontWeight: 800, color: DARK }}>Available Services</h2>
            <div style={{ width: 54, height: 3.5, background: TEAL_GRAD, borderRadius: 2, margin: "16px auto" }} />
            <p style={{ color: "#666", fontSize: 14.5, maxWidth: 640, margin: "0 auto", lineHeight: 1.7 }}>
              Booking talent is just the start — here's everything Momentsia handles alongside your show.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 26 }}>
            {availableServices.map(s => (
              <div key={s.title} style={{ background: "#f0fbf9", border: `1px solid #dcf3ef`, borderRadius: 16, padding: "26px 24px", display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{ color: T2, background: "#fff", width: 54, height: 54, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 10px rgba(14,207,176,0.15)" }}>
                  {s.icon}
                </div>
                <div>
                  <h3 style={{ fontSize: 15.5, fontWeight: 800, color: DARK, margin: "0 0 6px" }}>{s.title}</h3>
                  <p style={{ color: "#666", fontSize: 13, lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATIONS WE COVER */}
      <section style={{ position: "relative", background: NAVY, padding: "70px 24px", overflow: "hidden" }}>
        <ParticleNetwork />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 46 }}>
            <p style={{ color: T1, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 2.5 }}>Where We Perform</p>
            <h2 style={{ fontSize: "clamp(1.4rem,3vw,2.1rem)", fontWeight: 800, color: "#fff" }}>Locations We Cover</h2>
            <div style={{ width: 54, height: 3.5, background: TEAL_GRAD, borderRadius: 2, margin: "16px auto" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 22, marginBottom: 46 }}>
            {locations.map(loc => (
              <div key={loc.city} style={{
                background: loc.primary ? TEAL_GRAD : "rgba(255,255,255,0.05)",
                border: loc.primary ? "none" : "1px solid rgba(255,255,255,0.12)",
                borderRadius: 14, padding: "24px 20px", textAlign: "center",
              }}>
                <LocationOnIcon sx={{ fontSize: 26, color: loc.primary ? "#fff" : T1, marginBottom: "6px" }} />
                <h3 style={{ color: "#fff", fontWeight: 800, fontSize: 16, margin: "6px 0 6px" }}>{loc.city}</h3>
                <p style={{ color: loc.primary ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.55)", fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>{loc.note}</p>
              </div>
            ))}
          </div>

          {/* Corporate Office + Map */}
          <div className="office-map-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 26, alignItems: "stretch" }}>
            <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 16, padding: "30px 26px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <p style={{ color: T1, fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 2, marginBottom: 10 }}>Corporate Office</p>
              <h3 style={{ color: "#fff", fontWeight: 700, fontSize: 18, margin: "0 0 10px" }}>Momentsia P S Abacus</h3>
              <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 14, lineHeight: 1.8, margin: "0 0 18px" }}>
                703, 7th Floor, NH12, Action Area IIE, Newtown, Kolkata – 700161
              </p>
              <a href="tel:+918100364941" style={{ color: "#7ecbc4", textDecoration: "none", fontSize: 14, display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span>📞</span> +91 81003 64941
              </a>
              <a href="mailto:info@momentsia.com" style={{ color: "#7ecbc4", textDecoration: "none", fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span>✉</span> info@momentsia.com
              </a>
            </div>
            <div style={{ borderRadius: 16, overflow: "hidden", minHeight: 260, border: "1px solid rgba(255,255,255,0.12)" }}>
              <iframe
                title="Momentsia Office Location"
                src="https://www.google.com/maps?q=Action+Area+II,+Newtown,+Kolkata,+700161&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: 260, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA BAND — network + glow promotion strip */}
      <section style={{ position: "relative", overflow: "hidden", padding: "80px 24px", background: `linear-gradient(160deg, ${NAVY}, ${NAVY_DEEP})` }}>
        <ParticleNetwork maxParticles={70} />
        <div style={{ position: "absolute", top: "-12%", right: "-6%", width: 340, height: 340, borderRadius: "50%", background: `radial-gradient(circle, rgba(215,171,119,0.18), transparent 70%)`, filter: "blur(30px)", animation: "floatOrb 10s ease-in-out infinite", pointerEvents: "none", zIndex: 0 }} />
        <div style={{ position: "absolute", bottom: "-16%", left: "-6%", width: 280, height: 280, borderRadius: "50%", background: `radial-gradient(circle, rgba(215,171,119,0.12), transparent 70%)`, filter: "blur(30px)", animation: "floatOrb 12s ease-in-out infinite reverse", pointerEvents: "none", zIndex: 0 }} />
        <div style={{ position: "absolute", top: "38%", left: "50%", width: 220, height: 220, borderRadius: "50%", background: `radial-gradient(circle, rgba(215,171,119,0.10), transparent 70%)`, filter: "blur(28px)", animation: "pulseOrb 7s ease-in-out infinite", pointerEvents: "none", zIndex: 0 }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <span style={{ display: "inline-block", background: "rgba(215,171,119,0.16)", color: GOLD, fontWeight: 700, fontSize: 12, letterSpacing: 1, padding: "7px 18px", borderRadius: 30, border: "1px solid rgba(215,171,119,0.3)", marginBottom: 18 }}>
            Free Curation
          </span>
          <h2 style={{ fontSize: "clamp(1.5rem,3.4vw,2.2rem)", color: "#fff", margin: "0 0 12px" }}>Not Sure Which Show Fits Your Event?</h2>
          <p style={{ fontSize: 14.5, color: "rgba(255,255,255,0.72)", margin: "0 0 28px", maxWidth: 560, marginLeft: "auto", marginRight: "auto", lineHeight: 1.7 }}>
            Tell us your event type, city and budget — our talent curators will shortlist the right performers for you within 24 hours.
          </p>
          <button onClick={() => openBooking()} style={{ background: TEAL_GRAD, color: "#fff", border: "none", padding: "14px 38px", borderRadius: 40, fontWeight: 700, fontSize: 14.5, cursor: "pointer", boxShadow: "0 10px 30px rgba(14,207,176,0.35)" }}>
            Get a Free Shortlist
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <Footer />

      {/* WhatsApp float */}
      <a href="https://wa.me/918100364941" target="_blank" rel="noreferrer" style={{ position: "fixed", bottom: 24, right: 24, width: 58, height: 58, borderRadius: "50%", background: "#25d366", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(37,211,102,0.4)", zIndex: 9998, textDecoration: "none", fontSize: 28 }}>💬</a>

      <BookArtistDialog open={bookOpen} onClose={() => setBookOpen(false)} artistName={selectedArtist} />
    </div>
  );
}